import React, { useState, useEffect, useMemo } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from '../../hooks/useTranslation';
import { useCustomerStore } from '../../stores/useCustomerStore';

import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Separator } from '../../components/ui/separator';
// Removed Command component - using simple custom implementation instead
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '../../components/ui/popover';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import {
  Check,
  ChevronsUpDown,
  QrCode,
  User,
  Users,
  X,
  Plus,
  Search,
} from 'lucide-react';
import { cn } from '../../utils';
import { ModernQRScannerDialog } from './ModernQRScannerDialog';
import { customersService } from '../../services/customersService';

interface ModernCustomerSelectorProps {
  disabled?: boolean;
}

export const ModernCustomerSelector: React.FC<ModernCustomerSelectorProps> = ({
  disabled = false,
}) => {
  const { translate } = useTranslation();
  const { watch, setValue } = useFormContext();
  const [open, setOpen] = useState(false);
  const [searchValue, setSearchValue] = useState('');
  const [showOtpInput, setShowOtpInput] = useState(false);
  const [isValidatingOtp, setIsValidatingOtp] = useState(false);
  const [otpInput, setOtpInput] = useState('');
  const [pendingCustomerId, setPendingCustomerId] = useState<string | null>(
    null
  );
  const [hasCheckedIn, setHasCheckedIn] = useState(false);
  const [hasMembership, setHasMembership] = useState(false);
  const [qrScannerOpen, setQrScannerOpen] = useState(false);
  const [createCustomerDialogOpen, setCreateCustomerDialogOpen] =
    useState(false);
  const [newCustomerName, setNewCustomerName] = useState('');
  const [isCreatingCustomer, setIsCreatingCustomer] = useState(false);
  const [fetchedCustomer, setFetchedCustomer] = useState<any>(null);
  const [isFetchingCustomer, setIsFetchingCustomer] = useState(false);
  const searchInputRef = React.useRef<HTMLInputElement>(null);
  const isUpdatingSearchRef = React.useRef(false);
  const searchValueRef = React.useRef('');
  const searchTimeoutRef = React.useRef<NodeJS.Timeout | null>(null);

  const tempCustomerId = watch('tempCustomerId');
  const customerPoints = watch('customerPoints');

  const { customers, isLoading, fetchCustomers, checkinCustomer } =
    useCustomerStore();

  // Fetch customers when popover opens
  useEffect(() => {
    if (open && !createCustomerDialogOpen) {
      fetchCustomers(searchValue);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, createCustomerDialogOpen]);

  // Fetch customer separately if tempCustomerId exists but customer is not in the list
  useEffect(() => {
    if (!tempCustomerId) {
      setFetchedCustomer(null);
      return;
    }

    // Check if customer is already in the list
    const customerInList = customers.find(
      (c) =>
        c.id?.toString() === tempCustomerId?.toString() || c.id === tempCustomerId
    );

    if (customerInList) {
      // Customer is in list, clear fetched customer
      setFetchedCustomer(null);
      return;
    }

    // Customer not in list, fetch it
    const fetchCustomerById = async () => {
      try {
        setIsFetchingCustomer(true);
        const customerData = await customersService.getCustomer(
          String(tempCustomerId)
        );
        setFetchedCustomer(customerData);
      } catch (error) {
        console.error('Error fetching customer:', error);
        setFetchedCustomer(null);
      } finally {
        setIsFetchingCustomer(false);
      }
    };

    fetchCustomerById();
  }, [tempCustomerId, customers]);

  // Auto-focus search input when popover opens and reset search
  useEffect(() => {
    if (open) {
      // Clear any pending timeouts
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
        searchTimeoutRef.current = null;
      }

      // Reset search when opening
      setSearchValue('');
      lastFetchedSearchRef.current = '';
      searchValueRef.current = '';
      isUpdatingSearchRef.current = false;

      // Clear the input value directly
      if (searchInputRef.current) {
        searchInputRef.current.value = '';
      }

      // Small delay to ensure popover is fully rendered
      setTimeout(() => {
        searchInputRef.current?.focus();
      }, 100);
    } else {
      // Clear search when closing
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
        searchTimeoutRef.current = null;
      }
      setSearchValue('');
      if (searchInputRef.current) {
        searchInputRef.current.value = '';
      }
    }
  }, [open]);

  const handleCheckin = async (customerId: string, otp: string) => {
    try {
      const result = await checkinCustomer(customerId, otp);

      if (result.success && result.customerId) {
        setValue('customerId', result.customerId);
        setValue('tempCustomerId', result.customerId);
        setValue('hasCheckedIn', true);
        setValue('customerPoints', result.points || 0);
        setHasCheckedIn(true);
        setHasMembership(result.hasMembership || false);
        setShowOtpInput(false);
      } else if (result.error === 'Profile incomplete' && result.customerId) {
        // Handle profile incomplete case
        console.log('Profile incomplete for customer:', result.customerId);
      } else {
        throw new Error(result.error || 'Check-in failed');
      }
    } catch (error) {
      console.error('Check-in error:', error);
      throw error;
    }
  };

  // Find selected customer - check both the customers list and fetched customer
  const selectedCustomerInList = customers.find(
    (c) =>
      c.id?.toString() === tempCustomerId?.toString() || c.id === tempCustomerId
  );
  
  // Use fetched customer if not in list
  const selectedCustomer = selectedCustomerInList || fetchedCustomer;

  // Use a ref to prevent infinite loops from repeated calls
  const isSelectingRef = React.useRef(false);
  const lastSelectedCustomerRef = React.useRef<string | null>(null);
  const lastFetchedSearchRef = React.useRef<string>('');

  const handleCustomerSelect = React.useCallback(
    (customerId: string) => {
      // Prevent infinite loops
      if (isSelectingRef.current) {
        console.warn('⚠️ Already selecting customer, ignoring duplicate call');
        return;
      }

      console.log('handleCustomerSelect called with:', customerId);
      if (!customerId) {
        console.warn('⚠️ handleCustomerSelect called with empty customerId');
        return;
      }

      // Prevent duplicate selections using ref instead of watch to avoid re-renders
      if (lastSelectedCustomerRef.current === customerId) {
        console.log('⚠️ Customer already selected, ignoring');
        return;
      }

      isSelectingRef.current = true;
      lastSelectedCustomerRef.current = customerId;

      try {
        // Batch all setValue calls - React 18+ batches these automatically
        setValue('tempCustomerId', customerId, { shouldValidate: true, shouldDirty: true });
        setValue('customerId', customerId, { shouldValidate: true, shouldDirty: true });
        setValue('customerPoints', 0, { shouldDirty: true });
        setValue('hasCheckedIn', false, { shouldDirty: true });

        setHasCheckedIn(false);
        setHasMembership(false);
        setShowOtpInput(false);
        setPendingCustomerId(null);
        setOpen(false);
        setSearchValue('');

        console.log('✅ Form values set:', {
          tempCustomerId: customerId,
          customerId,
        });

        // Reset the ref after state updates complete
        // Use requestAnimationFrame to ensure it happens after render
        requestAnimationFrame(() => {
          isSelectingRef.current = false;
        });
      } catch (error) {
        console.error('❌ Error setting form values:', error);
        isSelectingRef.current = false;
        lastSelectedCustomerRef.current = null;
      }
    },
    [setValue]
  );

  const handleClearCustomer = () => {
    setValue('tempCustomerId', undefined, { shouldValidate: true, shouldDirty: true });
    setValue('customerId', undefined, { shouldValidate: true, shouldDirty: true });
    setValue('customerPoints', null, { shouldDirty: true });
    setValue('hasCheckedIn', false, { shouldDirty: true });
    setHasCheckedIn(false);
    setHasMembership(false);
    setShowOtpInput(false);
    setPendingCustomerId(null);
    setSearchValue('');
  };

  const handleCreateCustomer = async () => {
    if (!newCustomerName.trim()) return;

    setIsCreatingCustomer(true);
    try {
      // Split name into firstName and lastName
      const nameParts = newCustomerName.trim().split(/\s+/);
      const firstName = nameParts[0] || '';
      const lastName = nameParts.slice(1).join(' ') || '';

      const newCustomer = await customersService.createCustomer({
        firstName,
        lastName,
        email: '',
        phone: '',
      });

      // Refresh customer list
      await fetchCustomers('');

      // Automatically select the newly created customer
      handleCustomerSelect(newCustomer.id);

      // Close dialog and reset
      setCreateCustomerDialogOpen(false);
      setNewCustomerName('');
      setSearchValue('');
    } catch (error) {
      console.error('Error creating customer:', error);
      alert(
        translate(
          'pos.orders.messages.createCustomerError',
          'Failed to create customer'
        )
      );
    } finally {
      setIsCreatingCustomer(false);
    }
  };

  const handleOtpSubmit = async () => {
    if (!pendingCustomerId || !otpInput) return;

    setIsValidatingOtp(true);
    try {
      await handleCheckin(pendingCustomerId, otpInput);
      // Success is handled by the onCheckinSuccess callback
      setOtpInput('');
      setPendingCustomerId(null);
    } catch (error) {
      console.error('OTP validation error:', error);
      setOtpInput('');
    } finally {
      setIsValidatingOtp(false);
    }
  };

  const handleQrScan = async (customerId: string, otp: string) => {
    try {
      // Use the existing checkin logic for QR scan
      await handleCheckin(customerId, otp);
      // Success is handled by the onCheckinSuccess callback
    } catch (error) {
      console.error('QR scan error:', error);
    }
  };

  const getCustomerDisplayName = (customer: any) => {
    // Handle customers with firstName/lastName
    const fullName = `${customer.firstName || ''} ${
      customer.lastName || ''
    }`.trim();

    // Handle Zalo customers that might have 'name' instead of firstName/lastName
    if (fullName) {
      return fullName;
    }

    // Check for 'name' field (Zalo customers)
    if (customer.name) {
      return customer.name;
    }

    // Fallback
    return translate('pos.orders.messages.unknownCustomer');
  };

  // Memoize filtered customers to prevent unnecessary re-renders
  // When searchValue is provided, backend handles filtering, so no client-side filtering needed
  const filteredCustomers = useMemo(() => {
    // If we have a search value, backend already filtered the results
    // Only do client-side filtering if searchValue is empty (showing all customers)
    if (!searchValue) return customers;

    // Backend handles search, but we can do a light client-side filter as fallback
    // This helps with cases where backend might return slightly more results
    const searchLower = searchValue.toLowerCase();
    return customers.filter((customer) => {
      const displayName = getCustomerDisplayName(customer).toLowerCase();
      const email = (customer.email || '').toLowerCase();
      const phone = (customer.phone || '').toLowerCase();
      const name = (customer.name || '').toLowerCase();
      return (
        displayName.includes(searchLower) ||
        email.includes(searchLower) ||
        phone.includes(searchLower) ||
        name.includes(searchLower)
      );
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customers, searchValue]);

  return (
    <div className="space-y-4">
      {/* Customer Selection */}
      <div className="space-y-2">
        <Label className="text-sm font-medium">
          {translate('pos.orders.fields.customer')}
        </Label>

        <div className="flex gap-2">
          <Popover open={open} onOpenChange={setOpen}>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                role="combobox"
                aria-expanded={open}
                className="flex-1 justify-between"
                disabled={disabled}
              >
                {selectedCustomer ? (
                  <div className="flex items-center gap-2">
                    <User className="h-4 w-4" />
                    <span>{getCustomerDisplayName(selectedCustomer)}</span>
                    {hasCheckedIn && (
                      <Badge variant="secondary" className="ml-auto">
                        {translate('pos.orders.fields.checkedIn')}
                      </Badge>
                    )}
                  </div>
                ) : (
                  <div className="flex items-center gap-2">
                    <Users className="h-4 w-4" />
                    <span>
                      {translate('pos.orders.messages.selectCustomer')}
                    </span>
                  </div>
                )}
                <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
              </Button>
            </PopoverTrigger>
            <PopoverContent
              className="w-[400px] p-0"
              onOpenAutoFocus={(e) => {
                e.preventDefault();
                setTimeout(() => {
                  searchInputRef.current?.focus();
                }, 0);
              }}
            >
              <div className="flex flex-col">
                {/* Search Input */}
                <div className="flex items-center border-b px-3">
                  <Search className="mr-2 h-4 w-4 shrink-0 opacity-50" />
                  <Input
                    ref={searchInputRef}
                    placeholder={translate(
                      'pos.orders.messages.searchCustomer'
                    )}
                    value={searchValue}
                    onChange={(e) => {
                      const value = e.target.value;
                      setSearchValue(value);
                      // Clear any existing timeout
                      if (searchTimeoutRef.current) {
                        clearTimeout(searchTimeoutRef.current);
                      }
                      // Debounce API calls - reduced to 150ms for faster response
                      // Only search if query is at least 2 characters or empty (to show all)
                      const shouldSearch =
                        value.length === 0 || value.length >= 2;
                      searchTimeoutRef.current = setTimeout(() => {
                        if (value !== searchValueRef.current) {
                          searchValueRef.current = value;
                          if (
                            open &&
                            !createCustomerDialogOpen &&
                            shouldSearch
                          ) {
                            fetchCustomers(value);
                          } else if (value.length === 1) {
                            // Clear results if only 1 character (waiting for more)
                            setSearchValue(value);
                          }
                        }
                        searchTimeoutRef.current = null;
                      }, 150);
                    }}
                    className="border-0 focus-visible:ring-0 focus-visible:ring-offset-0 h-11"
                  />
                </div>

                {/* Results List */}
                <div className="max-h-[300px] overflow-y-auto">
                  {isLoading ? (
                    <div className="py-6 text-center text-sm text-muted-foreground">
                      {translate('pos.orders.messages.loading')}...
                    </div>
                  ) : filteredCustomers.length === 0 && !searchValue.trim() ? (
                    <div className="py-6 text-center text-sm text-muted-foreground">
                      {translate('pos.orders.messages.noCustomersFound')}
                    </div>
                  ) : filteredCustomers.length === 0 && searchValue.trim() ? (
                    <div className="py-4 px-2">
                      <div className="text-sm text-muted-foreground mb-2">
                        {translate(
                          'pos.orders.messages.noCustomersFound'
                        )}
                      </div>
                      <button
                        type="button"
                        onClick={() => {
                          if (!createCustomerDialogOpen) {
                            setNewCustomerName(searchValue);
                            setCreateCustomerDialogOpen(true);
                            setOpen(false);
                          }
                        }}
                        className="flex items-center w-full px-2 py-1.5 text-sm rounded-sm hover:bg-accent cursor-pointer"
                      >
                        <Plus className="mr-2 h-4 w-4" />
                        {translate(
                          'pos.orders.actions.createCustomer',
                          `Create "${searchValue}"`
                        )}
                      </button>
                    </div>
                  ) : (
                    <div className="p-1">
                      {filteredCustomers.map((customer) => {
                        const customerIdStr =
                          customer.id?.toString() || String(customer.id);
                        const displayName = getCustomerDisplayName(customer);
                        const isSelected =
                          tempCustomerId?.toString() ===
                            customer.id?.toString() ||
                          tempCustomerId === customer.id;

                        return (
                          <button
                            key={customer.id}
                            type="button"
                            onClick={() => {
                              if (
                                lastSelectedCustomerRef.current !==
                                  customerIdStr &&
                                !isSelectingRef.current
                              ) {
                                handleCustomerSelect(customerIdStr);
                              }
                            }}
                            className={cn(
                              'flex items-center w-full px-2 py-1.5 text-sm rounded-sm hover:bg-accent cursor-pointer',
                              isSelected && 'bg-accent'
                            )}
                          >
                            <Check
                              className={cn(
                                'mr-2 h-4 w-4',
                                isSelected ? 'opacity-100' : 'opacity-0'
                              )}
                            />
                            <div className="flex flex-col text-left">
                              <span className="font-medium">{displayName}</span>
                              {customer.phone && (
                                <span className="text-sm text-muted-foreground">
                                  {customer.phone}
                                </span>
                              )}
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>
            </PopoverContent>
          </Popover>

          {/* QR Scan Button */}
          <Button
            variant="outline"
            size="icon"
            disabled={disabled || hasCheckedIn}
            onClick={() => setQrScannerOpen(true)}
          >
            <QrCode className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* OTP Input */}
      {showOtpInput && (
        <div className="space-y-2 p-4 border rounded-lg bg-muted/50">
          <Label className="text-sm font-medium">
            {translate('pos.orders.fields.enterOtp')}
          </Label>
          <div className="flex gap-2">
            <Input
              type="text"
              placeholder="000000"
              value={otpInput}
              onChange={(e) => setOtpInput(e.target.value)}
              maxLength={6}
              className="flex-1"
              disabled={isValidatingOtp}
            />
            <Button
              onClick={handleOtpSubmit}
              disabled={!otpInput || otpInput.length !== 6 || isValidatingOtp}
              size="sm"
            >
              {isValidatingOtp ? (
                <div className="h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
              ) : (
                translate('pos.orders.actions.verify')
              )}
            </Button>
          </div>
        </div>
      )}

      {/* Customer Info Display */}
      {selectedCustomer && (
        <div className="space-y-2 p-4 border rounded-lg">
          <div className="flex items-center justify-between">
            <h4 className="font-medium">
              {getCustomerDisplayName(selectedCustomer)}
            </h4>
            <div className="flex items-center gap-2">
              {hasCheckedIn && (
                <Badge variant="default">
                  {translate('pos.orders.fields.checkedIn')}
                </Badge>
              )}
              <Button
                variant="ghost"
                size="sm"
                onClick={handleClearCustomer}
                disabled={disabled}
                className="h-6 w-6 p-0"
                title={translate(
                  'pos.orders.actions.clearCustomer',
                  'Clear customer selection'
                )}
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
          </div>

          {!hasCheckedIn && (
            <div className="space-y-2">
              <p className="text-xs text-muted-foreground">
                {translate(
                  'pos.orders.messages.customerTaggedForMetrics'
                )}
              </p>
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  setPendingCustomerId(selectedCustomer.id);
                  setShowOtpInput(true);
                }}
                className="w-full"
                disabled={disabled}
              >
                {translate('pos.orders.actions.checkInForBenefits')}
              </Button>
            </div>
          )}

          {hasMembership && (
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Badge variant="secondary">
                {translate('pos.orders.fields.member')}
              </Badge>
              <span>
                {translate('pos.orders.fields.points')}:{' '}
                {customerPoints || 0}
              </span>
            </div>
          )}
        </div>
      )}

      <Separator />

      {/* QR Scanner Dialog */}
      <ModernQRScannerDialog
        open={qrScannerOpen}
        onClose={() => setQrScannerOpen(false)}
        onSuccess={handleQrScan}
        onError={(error) => {
          console.error('QR Scanner error:', error);
          // You might want to show a notification here
        }}
      />

      {/* Create Customer Dialog */}
      <Dialog
        open={createCustomerDialogOpen}
        onOpenChange={setCreateCustomerDialogOpen}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {translate(
                'pos.orders.actions.createCustomer',
                'Create New Customer'
              )}
            </DialogTitle>
            <DialogDescription>
              {translate(
                'pos.orders.messages.createCustomerDescription',
                'Enter the customer name to create a new customer profile.'
              )}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="customerName">
                {translate(
                  'pos.orders.fields.customerName',
                  'Customer Name'
                )}
              </Label>
              <Input
                id="customerName"
                value={newCustomerName}
                onChange={(e) => setNewCustomerName(e.target.value)}
                placeholder={translate(
                  'pos.orders.messages.enterCustomerName',
                  'Enter customer name'
                )}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && newCustomerName.trim()) {
                    e.preventDefault();
                    handleCreateCustomer();
                  }
                }}
                autoFocus
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setCreateCustomerDialogOpen(false);
                setNewCustomerName('');
              }}
              disabled={isCreatingCustomer}
            >
              {translate('pos.common.action.cancel', 'Cancel')}
            </Button>
            <Button
              onClick={handleCreateCustomer}
              disabled={!newCustomerName.trim() || isCreatingCustomer}
            >
              {isCreatingCustomer
                ? translate(
                    'pos.orders.messages.creatingCustomer',
                    'Creating...'
                  )
                : translate('pos.common.action.save', 'Create')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
